import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { Album, AuthUser, Organization, Photo } from '@nargus/shared';
import {
  Download,
  FolderPlus,
  LoaderCircle,
  Plus,
  Search,
  Share2,
  Star,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useAlbums, useCreateAlbum } from '@/resources/album/album.service';
import {
  photoLoader,
  photoThumbnailLoader,
  useAddPhoto,
  useDeletePhotos,
  useDownloadPhotos,
  usePhotos,
  useUpdatePhoto,
  useUpdatePhotos,
} from '@/resources/photo/photo.service';
import { MAX_FILE_BYTES } from '@/lib/files';
import { ACCEPTED_TYPES, kindOf } from '@/lib/media';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PhotoGrid } from './photo-grid';
import { PhotoLightbox } from './photo-lightbox';
import { PhotoShareDialog } from './photo-share-dialog';
import { ConfirmDialog, EmptyNote, ErrorNote, SectionHeader } from './ui';

type Sort = 'newest' | 'oldest';

interface Progress {
  done: number;
  total: number;
}

export function PhotoLibrary({
  organization,
  user,
  album,
  title,
  hint,
  actions,
}: {
  organization: Organization;
  user: AuthUser;
  album?: Album;
  title: string;
  hint?: string;
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  const picker = useRef<HTMLInputElement>(null);

  const [progress, setProgress] = useState<Progress | null>(null);
  const [skipped, setSkipped] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('newest');
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openAt, setOpenAt] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Photo[] | null>(null);
  const [pickingAlbum, setPickingAlbum] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [dragging, setDragging] = useState(false);

  const photos = usePhotos(organization.id, album?.id);
  const add = useAddPhoto();
  const updateOne = useUpdatePhoto();
  const updateMany = useUpdatePhotos();
  const removeMany = useDeletePhotos();
  const downloadAll = useDownloadPhotos();

  const busy = progress !== null || updateMany.isPending || removeMany.isPending;
  const all = photos.data ?? [];

  const term = search.trim().toLowerCase();
  const visible = all
    .filter((photo) => !onlyFavorites || photo.favorite)
    .filter(
      (photo) =>
        !term ||
        photo.filename.toLowerCase().includes(term) ||
        (photo.caption ?? '').toLowerCase().includes(term),
    );
  const ordered = sort === 'newest' ? visible : visible.slice().reverse();

  const chosen = ordered.filter((photo) => selected.has(photo.id));
  const mayDelete = (photo: Photo) =>
    organization.role === 'owner' || photo.uploadedBy === user.id;
  const canDeleteChosen = chosen.length > 0 && chosen.every(mayDelete);

  async function uploadFiles(files: File[]) {
    if (files.length === 0) return;

    const media = files.filter((file) => kindOf(file.type));
    const fitting = media.filter((file) => file.size <= MAX_FILE_BYTES);
    setSkipped(files.length - fitting.length);
    if (fitting.length === 0) return;

    for (const [index, file] of fitting.entries()) {
      setProgress({ done: index, total: fitting.length });
      try {
        await add.mutateAsync({ organizationId: organization.id, file, albumId: album?.id });
      } catch {
        break;
      }
    }
    setProgress(null);
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void uploadFiles(files);
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDragging(false);
    void uploadFiles(Array.from(event.dataTransfer.files));
  }

  const pasteUpload = useRef(uploadFiles);
  pasteUpload.current = uploadFiles;

  useEffect(() => {
    function onPaste(event: ClipboardEvent) {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length > 0) void pasteUpload.current(files);
    }

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  function toggle(photo: Photo) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(photo.id)) next.delete(photo.id);
      else next.add(photo.id);
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  function setFavorites(favorite: boolean) {
    updateMany.mutate(
      { organizationId: organization.id, photoIds: [...selected], favorite },
      { onSuccess: clearSelection },
    );
  }

  return (
    <div
      className="flex flex-col gap-5"
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setDragging(false);
        }
      }}
      onDrop={onDrop}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SectionHeader title={title} hint={hint} />
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <input
            ref={picker}
            type="file"
            multiple
            accept={ACCEPTED_TYPES}
            className="hidden"
            onChange={onPick}
          />
          <Button
            type="button"
            disabled={progress !== null}
            onClick={() => picker.current?.click()}
          >
            {progress ? <LoaderCircle className="size-4 animate-spin" /> : <Upload />}
            {progress
              ? t('photos.uploading', { done: progress.done + 1, total: progress.total })
              : t('photos.add')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-48 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            placeholder={t('photos.searchPlaceholder')}
            className="pl-8"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <Button
          type="button"
          variant={onlyFavorites ? 'default' : 'outline'}
          aria-pressed={onlyFavorites}
          onClick={() => setOnlyFavorites(!onlyFavorites)}
        >
          <Star className={onlyFavorites ? 'fill-current' : undefined} />
          {t('photos.favorites')}
        </Button>

        <Select
          items={{
            newest: t('photos.sortNewest'),
            oldest: t('photos.sortOldest'),
          }}
          value={sort}
          onValueChange={(value) => setSort(String(value ?? 'newest') as Sort)}
        >
          <SelectTrigger className="w-40" aria-label={t('photos.sort')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">{t('photos.sortNewest')}</SelectItem>
            <SelectItem value="oldest">{t('photos.sortOldest')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-14 z-2 flex flex-wrap items-center gap-2 rounded-lg border bg-background/95 p-2 backdrop-blur">
          <span className="px-1 text-sm font-medium">
            {t('photos.selectedCount', { count: selected.size })}
          </span>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => setFavorites(true)}
          >
            <Star />
            {t('photos.favoriteAdd')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => setFavorites(false)}
          >
            <Star />
            {t('photos.favoriteRemove')}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => setPickingAlbum(true)}
          >
            <FolderPlus />
            {t('photos.toAlbum')}
          </Button>

          {album && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() =>
                updateMany.mutate(
                  {
                    organizationId: organization.id,
                    photoIds: [...selected],
                    removeAlbumId: album.id,
                  },
                  { onSuccess: clearSelection },
                )
              }
            >
              <X />
              {t('photos.fromAlbum')}
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => setSharing(true)}
          >
            <Share2 />
            {t('photos.share')}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || downloadAll.isPending}
            onClick={() =>
              downloadAll.mutate({ organizationId: organization.id, photos: chosen })
            }
          >
            {downloadAll.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Download />
            )}
            {t('common.download')}
          </Button>

          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={busy || !canDeleteChosen}
            onClick={() => setConfirmDelete(chosen)}
          >
            <Trash2 />
            {t('common.delete')}
          </Button>

          <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
            {t('photos.clearSelection')}
          </Button>
        </div>
      )}

      {skipped > 0 && (
        <p className="text-sm text-destructive">{t('photos.skipped', { count: skipped })}</p>
      )}
      <ErrorNote
        error={add.error ?? updateMany.error ?? removeMany.error ?? photos.error}
      />

      {photos.isPending ? (
        <ul className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
          {Array.from({ length: 12 }, (_, index) => (
            <li key={index}>
              <Skeleton className="aspect-square w-full rounded-md" />
            </li>
          ))}
        </ul>
      ) : ordered.length === 0 ? (
        <EmptyNote>{all.length === 0 ? t('photos.none') : t('photos.noMatches')}</EmptyNote>
      ) : (
        <PhotoGrid
          photos={ordered}
          loadThumbnail={photoThumbnailLoader(organization.id)}
          selected={selected}
          onSelect={toggle}
          onOpen={(photo) => setOpenAt(ordered.indexOf(photo))}
        />
      )}

      {dragging && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-background/80">
          <p className="rounded-lg border-2 border-dashed px-6 py-4 text-sm font-medium">
            {t('photos.dropHere')}
          </p>
        </div>
      )}

      {openAt !== null && ordered.length > 0 && (
        <PhotoLightbox
          photos={ordered}
          index={Math.min(openAt, ordered.length - 1)}
          onIndex={setOpenAt}
          onClose={() => setOpenAt(null)}
          load={photoLoader(organization.id)}
          canModify={mayDelete}
          onToggleFavorite={(photo) =>
            updateOne.mutate({
              organizationId: organization.id,
              photoId: photo.id,
              favorite: !photo.favorite,
            })
          }
          onSaveCaption={(photo, caption) =>
            updateOne.mutate({
              organizationId: organization.id,
              photoId: photo.id,
              caption: caption.trim() || null,
            })
          }
          onDelete={(photo) => setConfirmDelete([photo])}
        />
      )}

      {pickingAlbum && (
        <AlbumPicker
          organization={organization}
          busy={updateMany.isPending}
          onClose={() => setPickingAlbum(false)}
          onPick={(albumId) =>
            updateMany.mutate(
              { organizationId: organization.id, photoIds: [...selected], addAlbumId: albumId },
              {
                onSuccess: () => {
                  setPickingAlbum(false);
                  clearSelection();
                },
              },
            )
          }
        />
      )}

      {sharing && (
        <PhotoShareDialog
          organization={organization}
          photoIds={[...selected]}
          onClose={() => setSharing(false)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={t('photos.removeTitle', { count: confirmDelete.length })}
          description={t('photos.removeHint', { count: confirmDelete.length })}
          confirmLabel={t('common.delete')}
          busy={removeMany.isPending}
          error={removeMany.error}
          onClose={() => setConfirmDelete(null)}
          onConfirm={() =>
            removeMany.mutate(
              {
                organizationId: organization.id,
                photoIds: confirmDelete.map((photo) => photo.id),
              },
              {
                onSuccess: () => {
                  setConfirmDelete(null);
                  setOpenAt(null);
                  clearSelection();
                },
              },
            )
          }
        />
      )}
    </div>
  );
}

function AlbumPicker({
  organization,
  busy,
  onClose,
  onPick,
}: {
  organization: Organization;
  busy: boolean;
  onClose: () => void;
  onPick: (albumId: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const albums = useAlbums(organization.id);
  const create = useCreateAlbum();

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('photos.toAlbum')}</DialogTitle>
          <DialogDescription>{t('photos.toAlbumHint')}</DialogDescription>
        </DialogHeader>

        <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
          {albums.isPending ? (
            <Skeleton className="h-9 w-full" />
          ) : albums.data?.length === 0 ? (
            <EmptyNote>{t('albums.none')}</EmptyNote>
          ) : (
            albums.data?.map((album) => (
              <Button
                key={album.id}
                type="button"
                variant="ghost"
                className="justify-between"
                disabled={busy}
                onClick={() => onPick(album.id)}
              >
                <span className="truncate">{album.name}</span>
                <span className="text-xs text-muted-foreground">{album.photoCount}</span>
              </Button>
            ))
          )}
        </div>

        <div className="flex gap-2">
          <Input
            value={name}
            maxLength={80}
            placeholder={t('albums.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
          <Button
            type="button"
            variant="outline"
            disabled={!name.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { organizationId: organization.id, name: name.trim() },
                {
                  onSuccess: (data) => {
                    setName('');
                    onPick(data.album.id);
                  },
                },
              )
            }
          >
            {create.isPending ? <LoaderCircle className="size-4 animate-spin" /> : <Plus />}
            {t('albums.create')}
          </Button>
        </div>

        <ErrorNote error={create.error ?? albums.error} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
